import {Col, Input, Row, Typography} from 'antd';
import {useIntl} from 'react-intl';
import FormItem from '../../../../../shared/components/form-item';
import {OrderDetailsDto} from '../../../../../shared/services/orders/dto/order-details-dto';
import {shouldShowExternalShipmentNumber, isShipmentNumberEmphasized} from '../../../../../shared/services/orders/carrier-shipment-number';
import {formatDateWithSecondsTime} from 'src/shared/constants/AppConst';
import {dateFormat} from 'src/shared/functions/date-format';
import MainUtils from 'src/shared/utils/main';

interface IOrderDetailsInfoProps {
  orderDetails?: OrderDetailsDto;
}

const OrderDetailsInfo = (props: IOrderDetailsInfoProps) => {
  const {orderDetails} = props;

  const {messages} = useIntl();

  return (
    <Row gutter={[0, 20]}>
      <Col span={24}>
        <FormItem
          name='ShopName'
          isNotForm
          label={messages['product.shopName'].toString()}
          fieldCommponent={<Input value={orderDetails?.ShopName} disabled />}
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='orderNumber'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.orderNumber'
          ].toString()}
          fieldCommponent={<Input value={orderDetails?.OrderNumber} disabled />}
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='orderDate'
          isNotForm
          label={messages['orders.orderDetails.orderInfo.orderDate'].toString()}
          fieldCommponent={
            <Input
              value={
                orderDetails?.OrderDate &&
                dateFormat(orderDetails?.OrderDate, formatDateWithSecondsTime)
              }
              disabled
            />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='subTotalCash'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.subTotalCash'
          ].toString()}
          fieldCommponent={
            <Input value={orderDetails?.SubTotalCash} disabled />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='taxes'
          isNotForm
          label={messages['orders.orderDetails.orderInfo.taxes'].toString()}
          fieldCommponent={<Input value={orderDetails?.Taxes} disabled />}
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='shippingCost'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.shippingCost'
          ].toString()}
          fieldCommponent={
            <Input value={orderDetails?.ShippingCost} disabled />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='totalCash'
          isNotForm
          label={messages['orders.orderDetails.orderInfo.totalCash'].toString()}
          fieldCommponent={<Input value={orderDetails?.TotalCash} disabled />}
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='itemsCount'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.itemsCount'
          ].toString()}
          fieldCommponent={<Input value={orderDetails?.ItemsCount} disabled />}
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='status'
          isNotForm
          label={messages['orders.orderDetails.orderInfo.status'].toString()}
          fieldCommponent={<Input value={orderDetails?.Status} disabled />}
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='paymentStatus'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.paymentStatus'
          ].toString()}
          fieldCommponent={
            <Input value={orderDetails?.PaymentStatus} disabled />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='customerFullName'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.customerFullName'
          ].toString()}
          fieldCommponent={
            <Input value={orderDetails?.Customer.FullName} disabled />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem
          name='customerEmail'
          isNotForm
          label={messages[
            'orders.orderDetails.orderInfo.customerEmail'
          ].toString()}
          fieldCommponent={
            <Input value={orderDetails?.Customer.Email} disabled />
          }
        />
      </Col>

      <Col span={24}>
        <FormItem name='liveShipmentStatus' isNotForm
          label={messages['orders.orderDetails.orderInfo.liveShipmentStatus'].toString()}
          fieldCommponent={<Input value={orderDetails?.LiveShipmentStatusTranslated || orderDetails?.LiveShipmentStatus || '—'} disabled />} />
      </Col>
      <Col span={24}>
        <FormItem
          name='externalShipmentOrderNumber'
          isNotForm
          label={messages['orders.orderDetails.orderInfo.shipmentNumber'].toString()}
          fieldCommponent={
            shouldShowExternalShipmentNumber(orderDetails) ? (
              <Typography.Text
                copyable={{text: String(orderDetails?.ExternalShipmentOrderNumber ?? '')}}
                strong={isShipmentNumberEmphasized(orderDetails)}
                style={{direction: 'ltr', unicodeBidi: 'isolate'}}>
                <span dir='ltr'>{orderDetails?.ExternalShipmentOrderNumber}</span>
              </Typography.Text>
            ) : (
              <span className='text-muted'>—</span>
            )
          }
        />
      </Col>
      <Col span={24}>
        <FormItem name='needsPmSignOff' isNotForm
          label={messages['orders.orderDetails.orderInfo.needsPmSignOff'].toString()}
          fieldCommponent={<Input value={orderDetails?.NeedsPmSignOff ? messages['common.yes'].toString() : messages['common.no'].toString()} disabled />} />
      </Col>
      {orderDetails?.Status === 'Rejected' && (
        <Col span={24}>
          <FormItem name='rejectionReason' isNotForm
            label={messages['orders.orderDetails.orderInfo.rejectionReason'].toString()}
            fieldCommponent={<Input.TextArea value={orderDetails?.Reason ?? ''} rows={3} disabled />} />
        </Col>
      )}
      {!MainUtils.isEmptyValue(orderDetails?.LastBookingError) && (
        <Col span={24}>
          <FormItem name='lastBookingError' isNotForm
            label={messages['orders.orderDetails.orderInfo.courierBookingError'].toString()}
            fieldCommponent={<Input.TextArea value={orderDetails?.LastBookingError ?? ''} rows={3} disabled />} />
        </Col>
      )}
      {!MainUtils.isEmptyValue(orderDetails?.RejectionMessage) && (
        <Col span={24}>
          <FormItem name='courierRejectionMessage' isNotForm
            label={messages['orders.orderDetails.orderInfo.rejectedByCourier'].toString()}
            fieldCommponent={<Input.TextArea value={orderDetails?.RejectionMessage ?? ''} rows={3} disabled />} />
        </Col>
      )}
      {!MainUtils.isEmptyValue(orderDetails?.RebookWarning) && (
        <Col span={24}>
          <FormItem name='rebookWarning' isNotForm
            label={messages['orders.orderDetails.orderInfo.feeChange'].toString()}
            fieldCommponent={<Input.TextArea value={orderDetails?.RebookWarning ?? ''} rows={3} disabled />} />
        </Col>
      )}

      {orderDetails?.Status === 'DeliveryFailed' && (
        <>
          <Col span={24}>
            <Typography.Text strong className='text-title'>
              {messages['orderDetails.deliveryFailed.section'].toString()}
            </Typography.Text>
          </Col>
          <Col span={24}>
            <FormItem name='deliveryFailedReasonAr' isNotForm
              label={`${messages['orderDetails.deliveryFailed.reason'].toString()} (AR)`}
              fieldCommponent={<Input value={orderDetails?.DeliveryFailedReasonNameAr ?? '—'} disabled dir='rtl' />} />
          </Col>
          <Col span={24}>
            <FormItem name='deliveryFailedReasonEn' isNotForm
              label={`${messages['orderDetails.deliveryFailed.reason'].toString()} (EN)`}
              fieldCommponent={<Input value={orderDetails?.DeliveryFailedReasonNameEn ?? '—'} disabled />} />
          </Col>
          <Col span={24}>
            <FormItem name='deliveryFailedResponsibleParty' isNotForm
              label={messages['orderDetails.deliveryFailed.responsibleParty'].toString()}
              fieldCommponent={
                <Input
                  value={
                    orderDetails?.DeliveryFailedResponsibleParty
                      ? messages[`orderReasons.party.${orderDetails.DeliveryFailedResponsibleParty.toLowerCase()}`]?.toString() || '—'
                      : '—'
                  }
                  disabled
                />
              }
            />
          </Col>
          <Col span={24}>
            <FormItem name='deliveryFailedNoteAr' isNotForm
              label={messages['orderDetails.deliveryFailed.noteAr'].toString()}
              fieldCommponent={<Input.TextArea value={orderDetails?.DeliveryFailedNoteAr ?? ''} rows={3} disabled dir='rtl' />} />
          </Col>
          <Col span={24}>
            <FormItem name='deliveryFailedNoteEn' isNotForm
              label={messages['orderDetails.deliveryFailed.noteEn'].toString()}
              fieldCommponent={<Input.TextArea value={orderDetails?.DeliveryFailedNoteEn ?? ''} rows={3} disabled />} />
          </Col>
        </>
      )}
      <Col span={24}>
        <Typography.Text strong className='text-title'>
          {messages['orderDetails.payment.section'].toString()}
        </Typography.Text>
      </Col>
      <Col span={24}>
        <FormItem
          name='paymentMethod'
          isNotForm
          label={messages['orderDetails.payment.method'].toString()}
          fieldCommponent={
            <Input
              value={
                orderDetails?.CardPaymentDetails
                  ? `${messages['orderDetails.payment.card'].toString()} (${orderDetails.CardPaymentDetails.CardType} •••• ${orderDetails.CardPaymentDetails.Last4})`
                  : messages['orderDetails.payment.cashOnDelivery'].toString()
              }
              disabled
            />
          }
        />
      </Col>
      {orderDetails?.CardPaymentDetails && (
        <>
          <Col span={24}>
            <FormItem name='paymentCharged' isNotForm
              label={messages['orderDetails.payment.charged'].toString()}
              fieldCommponent={<Input value={`${orderDetails.CardPaymentDetails.ChargedAmount} ${orderDetails.CardPaymentDetails.ChargedCurrency}`} disabled dir='ltr' />} />
          </Col>
          <Col span={24}>
            <FormItem name='paymentOrderTotal' isNotForm
              label={messages['orderDetails.payment.orderTotal'].toString()}
              fieldCommponent={<Input value={`${orderDetails.CardPaymentDetails.AmountSyp} SYP`} disabled />} />
          </Col>
          <Col span={24}>
            <FormItem name='paymentRate' isNotForm
              label={messages['orderDetails.payment.rate'].toString()}
              fieldCommponent={<Input value={`1 USD = ${orderDetails.CardPaymentDetails.SypPerUsd} SYP`} disabled dir='ltr' />} />
          </Col>
          {orderDetails.CardPaymentDetails.MarkupPercent > 0 && (
            <Col span={24}>
              <FormItem name='paymentMarkup' isNotForm
                label={messages['orderDetails.payment.markup'].toString()}
                fieldCommponent={<Input value={`${orderDetails.CardPaymentDetails.MarkupPercent}%`} disabled dir='ltr' />} />
            </Col>
          )}
          <Col span={24}>
            <FormItem name='paymentTransaction' isNotForm
              label={messages['orderDetails.payment.transaction'].toString()}
              fieldCommponent={
                <Typography.Text
                  copyable={{text: String(orderDetails.CardPaymentDetails.TransactionId ?? '')}}
                  style={{direction: 'ltr', unicodeBidi: 'isolate'}}>
                  <span dir='ltr'>{orderDetails.CardPaymentDetails.TransactionId}</span>
                </Typography.Text>
              } />
          </Col>
          <Col span={24}>
            <FormItem name='paymentPaidOn' isNotForm
              label={messages['orderDetails.payment.paidOn'].toString()}
              fieldCommponent={<Input value={orderDetails.CardPaymentDetails.PaidOnUtc && dateFormat(orderDetails.CardPaymentDetails.PaidOnUtc, formatDateWithSecondsTime)} disabled />} />
          </Col>
        </>
      )}
    </Row>
  );
};

export default OrderDetailsInfo;
